export const OVERLAY_CONFIG_STORAGE_KEY = "kairos.viewportOverlay.v1";

export const OVERLAY_FONT_SIZE_MIN = 8;
export const OVERLAY_FONT_SIZE_MAX = 24;
export const OVERLAY_MEASUREMENT_FONT_SIZE_FALLBACK = 11;

const clampNumber = (value, min, max, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

export const measurementLabelFontSize = (cfg) => clampNumber(
  cfg?.measurementFontSize,
  OVERLAY_FONT_SIZE_MIN,
  OVERLAY_FONT_SIZE_MAX,
  OVERLAY_MEASUREMENT_FONT_SIZE_FALLBACK
);

export const OVERLAY_FIELDS = [
  { key: "patientName", label: "Patient name" },
  { key: "patientId", label: "MRN / patient ID" },
  { key: "accessionNumber", label: "Accession" },
  { key: "studyDescription", label: "Study description" },
  { key: "studyDate", label: "Study date" },
  { key: "modality", label: "Modality" },
  { key: "seriesDescription", label: "Series description" },
  { key: "seriesNumber", label: "Series number" },
  { key: "imageNumber", label: "Image number" },
  { key: "windowLevel", label: "WW/WL" },
  { key: "pixelSpacing", label: "Pixel spacing" },
  { key: "sliceThickness", label: "Slice thickness" },
  { key: "sliceLocation", label: "Slice location" },
  { key: "acquisitionDateTime", label: "Acquisition date/time" },
  { key: "lateralityView", label: "Laterality / view" },
];

export const OVERLAY_POSITIONS = [
  { key: "topLeft", label: "Top left" },
  { key: "topCenter", label: "Top center" },
  { key: "topRight", label: "Top right" },
  { key: "bottomLeft", label: "Bottom left" },
  { key: "bottomCenter", label: "Bottom center" },
  { key: "bottomRight", label: "Bottom right" },
];

export const DEFAULT_OVERLAY_CONFIG = {
  enabled: true,
  fontSize: 10,
  measurementFontSize: OVERLAY_MEASUREMENT_FONT_SIZE_FALLBACK,
  color: "#b7dce5",
  showPatientBanner: true,
  showViewportLabels: true,
  showDicomAttributes: true,
  showOrientationMarkers: true,
  density: "compact",
  positions: {
    topLeft: ["patientName", "patientId", "studyDescription"],
    topCenter: ["slotLabel"],
    topRight: ["imageNumber", "windowLevel", "pixelSpacing"],
    bottomLeft: ["modality", "seriesDescription", "seriesNumber"],
    bottomCenter: [],
    bottomRight: ["accessionNumber", "studyDate"],
  },
};

const safeJson = (raw) => {
  try { return JSON.parse(raw); } catch { return null; }
};

export const normalizeOverlayConfig = (cfg) => {
  const incoming = cfg && typeof cfg === "object" && !Array.isArray(cfg) ? cfg : {};
  const allowedFields = new Set(["slotLabel", ...OVERLAY_FIELDS.map(field => field.key)]);
  const positions = {};
  const assignedFields = new Set();
  for (const pos of OVERLAY_POSITIONS) {
    const fields = Array.isArray(incoming.positions?.[pos.key])
      ? incoming.positions[pos.key]
      : DEFAULT_OVERLAY_CONFIG.positions[pos.key];
    positions[pos.key] = fields.filter(field => {
      if (!allowedFields.has(field) || assignedFields.has(field)) return false;
      assignedFields.add(field);
      return true;
    });
  }
  const color = typeof incoming.color === "string" && /^#[0-9a-f]{6}$/i.test(incoming.color)
    ? incoming.color
    : DEFAULT_OVERLAY_CONFIG.color;
  const legacyMeasurementFontSize = Object.prototype.hasOwnProperty.call(incoming, "fontSize")
    ? clampNumber(incoming.fontSize, OVERLAY_FONT_SIZE_MIN, OVERLAY_FONT_SIZE_MAX, OVERLAY_MEASUREMENT_FONT_SIZE_FALLBACK)
    : OVERLAY_MEASUREMENT_FONT_SIZE_FALLBACK;
  return {
    enabled: incoming.enabled !== false,
    fontSize: clampNumber(incoming.fontSize, OVERLAY_FONT_SIZE_MIN, OVERLAY_FONT_SIZE_MAX, DEFAULT_OVERLAY_CONFIG.fontSize),
    measurementFontSize: clampNumber(incoming.measurementFontSize, OVERLAY_FONT_SIZE_MIN, OVERLAY_FONT_SIZE_MAX, legacyMeasurementFontSize),
    color,
    showPatientBanner: incoming.showPatientBanner !== false,
    showViewportLabels: incoming.showViewportLabels !== false,
    showDicomAttributes: incoming.showDicomAttributes !== false,
    showOrientationMarkers: incoming.showOrientationMarkers !== false,
    density: incoming.density === "comfortable" ? "comfortable" : "compact",
    positions,
  };
};

const PATIENT_BANNER_FIELDS = new Set(["patientName", "patientId"]);

export const overlayFieldVisible = (cfg, field) => {
  if (cfg?.enabled === false) return false;
  if (field === "slotLabel") return cfg?.showViewportLabels !== false;
  if (PATIENT_BANNER_FIELDS.has(field)) return cfg?.showPatientBanner !== false;
  return cfg?.showDicomAttributes !== false;
};

export const overlayToolRailSafeInsets = (cfg, options = {}) => {
  const normalized = normalizeOverlayConfig(cfg);
  const gutter = Math.max(0, Number(options.gutter) || 0);
  const topMinimum = Math.max(0, Number(options.topMinimum) || 0);
  const bottomMinimum = Math.max(0, Number(options.bottomMinimum) || 0);
  const lineHeight = Math.max(16, normalized.fontSize * (normalized.density === "comfortable" ? 1.6 : 1.35));
  const visibleCount = position => (normalized.positions?.[position] || [])
    .filter(field => overlayFieldVisible(normalized, field)).length;
  const topRequired = Math.max(
    4 + visibleCount("topLeft") * lineHeight,
    4 + visibleCount("topCenter") * lineHeight,
    4 + visibleCount("topRight") * lineHeight
  ) + gutter;
  const bottomRequired = Math.max(
    28 + visibleCount("bottomLeft") * lineHeight,
    28 + visibleCount("bottomCenter") * lineHeight,
    48 + visibleCount("bottomRight") * lineHeight
  ) + gutter;
  return {
    top: Math.max(topMinimum, Math.ceil(topRequired)),
    bottom: Math.max(bottomMinimum, Math.ceil(bottomRequired)),
  };
};

export const loadOverlayConfig = () => {
  if (typeof window === "undefined") return DEFAULT_OVERLAY_CONFIG;
  return normalizeOverlayConfig(safeJson(window.localStorage.getItem(OVERLAY_CONFIG_STORAGE_KEY)));
};

export const saveOverlayConfig = (cfg) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(OVERLAY_CONFIG_STORAGE_KEY, JSON.stringify(normalizeOverlayConfig(cfg)));
};
